import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { Alert, Button, Card, Checkbox, Divider, Form, Input, notification, Radio, Typography } from 'antd';
import {
    Price,
    UploadFile,
    SquarePaymentForm,
    AddressProps,
    ShippingCarriers,
    ShippingMethods,
    ErrorRender,
} from '@partsbadger/library';
import { SQUARE_APP_ID, SQUARE_ENVIRONMENT_URL, SQUARE_LOCATION_ID } from '@partsbadger/utils';
import ContactStore from '@/stores/ContactStore';
import ShowDefault from '@/components/SalesOrders/Addresses/ShowDefault';
import AddAddressModal from '@/components/SalesOrders/Addresses/AddAddressModal';
import { ContactProps, SalesOrderProps, OrderLineItemProps } from '@/types/types';
import { InfoCircleOutlined } from '@ant-design/icons';
import ShippingPrefsReadOnly from './ShippingPrefsReadOnly';
import SalesOrderStore from '@/stores/SalesOrderStore';
import ReadOnlyAddressCard from './ReadOnlyAddressCard';
const { Title, Text } = Typography;
import { validatePhoneNumber } from '@/utils/utils';

const findById = (list: AddressProps[], id?: number) => (id ? list.find(a => a.id === id) ?? null : null);

const getCountryCode = (addr?: AddressProps | null) => {
    const raw: any = addr?.country;
    return typeof raw === 'string' ? raw : raw?.code ?? raw?.iso2 ?? raw?.abbr ?? '';
};

const patchOrderAddressFromAddress = async (type: 'billing' | 'shipping', addr: AddressProps) => {
    const payload: any = {
        [`${type}_name`]: addr.name || '',
        [`${type}_street`]: addr.street || '',
        [`${type}_city`]: addr.city || '',
        [`${type}_state`]: addr.state || '',
        [`${type}_zip_code`]: (addr as any).zip_code || '',
        [`${type}_country`]: getCountryCode(addr) || '',
    };

    const orderId = SalesOrderStore?.order?.id; // assuming SalesOrderStore is in scope in this file; if not, pass in as prop or import like modal does
    if (!orderId) return;

    await SalesOrderStore.patchOrderDraft(orderId, payload);
};

export type PaymentMethod = 'credit_card' | 'purchase_order';

type SubmitOverrides = {
    phone?: string;
    email?: string;
    shippingAccountNumber?: string;
    carrier?: string;
    shippingMethod?: string;
    saveShippingDefaults?: boolean;
    shippingAddressId?: number;
    billingAddressId?: number;
    billingSameAsShipping?: boolean;
};

export interface OrderCheckoutFormData {
    shippingAddressId?: number;
    billingAddressId?: number;
    billingSameAsShipping: boolean;
    shippingAccountNumber?: string;
    carrier?: string;
    shippingMethod?: string;
    phone: string;
    email: string;
    paymentMethod: PaymentMethod;
    purchaseOrderInfo?: { poFiles?: any[] };
    saveShippingDefaults?: boolean;
    notes?: string;
}

type Props = {
    order: SalesOrderProps;
    contact: ContactProps;
    order_items: OrderLineItemProps[];
    formData: OrderCheckoutFormData;
    submitting: boolean;
    onFormUpdate: <K extends keyof OrderCheckoutFormData>(key: K, value: OrderCheckoutFormData[K]) => void;
    onSubmitPO: (tokenOverride?: string, overrides?: SubmitOverrides) => void;

    onCreditCardToken: (token: string, overrides?: SubmitOverrides) => void;

    onShippingAddressChange: (addressId: number) => void;
    onBillingAddressChange: (addressId: number) => void;
    onBillingSameAsShippingChange: (same: boolean) => void;
    mode?: 'checkout' | 'reorder';
};

const OrderCheckoutSummary: React.FC<Props> = observer(
    ({
        order,
        contact,
        formData,
        submitting,
        onFormUpdate,
        onSubmitPO,
        onCreditCardToken,
        onShippingAddressChange,
        onBillingAddressChange,
        onBillingSameAsShippingChange,
        mode = 'checkout',
    }) => {
        const isReorder = mode === 'reorder';

        const lastValidatedRef = useRef<SubmitOverrides>({});
        const allowSquareSubmitRef = useRef(false);
        const squareRespondedRef = useRef(false);
        const [form] = Form.useForm();
        const buttonRef = useRef<HTMLButtonElement>(null);
        const pendingMissingRef = useRef<string[]>([]);
        const isHobbyist = contact?.is_hobbyist === true;

        const [shippingModalOpen, setShippingModalOpen] = useState(false);
        const [billingModalOpen, setBillingModalOpen] = useState(false);
        const [editingShippingPrefs, setEditingShippingPrefs] = useState(false);
        const [shippingModalInitial, setShippingModalInitial] = useState<AddressProps | null | undefined>(null);
        const [billingModalInitial, setBillingModalInitial] = useState<AddressProps | null | undefined>(null);
        const [cardError, setCardError] = useState<string | null>(null);
        const [poAttempted, setPoAttempted] = useState(false);
        const [cardAttempted, setCardAttempted] = useState(false);
        const [ackAttempted, setAckAttempted] = useState(false);
        const [acks, setAcks] = useState({
            terms: false,
            privacy: false,
            authoritative: false,
            dimensional: false,
        });

        const setAck = (key: keyof typeof acks, value: boolean) => setAcks(prev => ({ ...prev, [key]: value }));

        const requiredAckKeys = isReorder
            ? ([] as const)
            : isHobbyist
            ? (['terms', 'privacy', 'authoritative', 'dimensional'] as const)
            : (['privacy', 'dimensional'] as const);

        const ackLabel: Record<string, string> = {
            terms: 'Terms and Conditions',
            privacy: 'Privacy & Terms Policy',
            authoritative: 'Authoritative sales order acknowledgment',
            dimensional: 'Dimensional tolerance acknowledgment',
        };

        const getMissingAcks = () => requiredAckKeys.filter(k => !acks[k]).map(k => ackLabel[k]);
        const ackError = (ok: boolean, msg: string) => (ackAttempted && !ok ? msg : undefined);

        const { billingAddresses, shippingAddresses, default_billing_address, default_shipping_address } = ContactStore;

        const pickDefault = (
            defaultAddr: AddressProps | null | undefined,
            list: AddressProps[]
        ): AddressProps | null => {
            if (defaultAddr && (defaultAddr as any)?.street) return defaultAddr;
            const primary = list.find(a => (a as any).primary);
            return primary ?? list[0] ?? null;
        };

        const currentShippingAddress = React.useMemo(() => pickDefault(default_shipping_address, shippingAddresses), [
            default_shipping_address,
            shippingAddresses,
        ]);

        const currentBillingAddress = React.useMemo(() => pickDefault(default_billing_address, billingAddresses), [
            default_billing_address,
            billingAddresses,
        ]);

        const selectedShippingAddress: AddressProps | null = React.useMemo(
            () => findById(shippingAddresses, formData.shippingAddressId) ?? currentShippingAddress ?? null,
            [shippingAddresses, formData.shippingAddressId, currentShippingAddress]
        );

        const roShippingAccount = (order as any)?.shipping_account_number ?? '';
        const roCarrier = (order as any)?.carrier ?? '';
        const roMethod = (order as any)?.shipping_method ?? '';

        useEffect(() => {
            // If user selected an address that no longer exists, repair it.
            if (formData.shippingAddressId && !existsIn(shippingAddresses, formData.shippingAddressId)) {
                const fallbackId = currentShippingAddress?.id;
                if (fallbackId) {
                    onFormUpdate('shippingAddressId', fallbackId);
                    onShippingAddressChange(fallbackId);

                    if (formData.billingSameAsShipping) {
                        onFormUpdate('billingAddressId', fallbackId);
                        onBillingAddressChange(fallbackId);
                    }
                } else {
                    // No addresses left — clear selection so UI forces user to add/select
                    onFormUpdate('shippingAddressId', undefined);
                    if (formData.billingSameAsShipping) onFormUpdate('billingAddressId', undefined);
                }
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [shippingAddresses.map(a => a.id).join(','), formData.shippingAddressId, currentShippingAddress?.id]);

        const selectedBillingAddress: AddressProps | null = React.useMemo(
            () => findById(billingAddresses, formData.billingAddressId) ?? currentBillingAddress ?? null,
            [billingAddresses, formData.billingAddressId, currentBillingAddress]
        );

        const canEditShippingPrefs = !submitting;
        // Force hobbyists to credit card
        useEffect(() => {
            if (isHobbyist && formData.paymentMethod !== 'credit_card') {
                onFormUpdate('paymentMethod', 'credit_card');
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [isHobbyist]);

        useEffect(() => {
            setPoAttempted(false);
        }, [formData.paymentMethod]);

        const seededShippingRef = useRef(false);
        useEffect(() => {
            if (seededShippingRef.current) return;
            if (!formData.shippingAddressId && currentShippingAddress?.id) {
                seededShippingRef.current = true;
                onFormUpdate('shippingAddressId', currentShippingAddress.id);
                onShippingAddressChange(currentShippingAddress.id);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [currentShippingAddress?.id, formData.shippingAddressId]);

        // If billing not same, seed billing once (no tax implications)
        const seededBillingRef = useRef(false);
        const existsIn = (list: AddressProps[], id?: number) => !!id && list.some(a => a.id === id);
        useEffect(() => {
            if (formData.billingSameAsShipping) {
                seededBillingRef.current = false;
                return;
            }
            if (seededBillingRef.current) return;
            if (!formData.billingAddressId && currentBillingAddress?.id) {
                seededBillingRef.current = true;
                onFormUpdate('billingAddressId', currentBillingAddress.id);
                onBillingAddressChange(currentBillingAddress.id);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [formData.billingSameAsShipping, currentBillingAddress?.id, formData.billingAddressId]);

        const normalizeSelectValue = (val: any): string | undefined => {
            if (val == null) return undefined;
            if (typeof val === 'string') return val;
            if (typeof val === 'number') return String(val);
            if (typeof val?.target?.value === 'string') return val.target.value;
            if (typeof val?.value === 'string') return val.value;
            if (typeof val?.key === 'string') return val.key;
            if (typeof val?.label === 'string') return val.label;
            try {
                return String(val);
            } catch {
                return undefined;
            }
        };

        const toNumber = (v: any) => {
            const n = typeof v === 'string' ? parseFloat(v) : Number(v);
            return Number.isFinite(n) ? n : 0;
        };

        const subtotal = toNumber(order.subtotal);
        const discount = toNumber(order.discount);
        const taxableBase = Math.max(0, subtotal - discount);

        const taxRate = toNumber(order.taxes_rate);
        const tax = taxRate > 0 ? taxableBase * (taxRate / 100) : 0;

        const serverTotal = toNumber(order.total_cost);
        const computedTotal = taxableBase + tax;
        const total = serverTotal > 0 ? serverTotal : computedTotal;

        useEffect(() => {
            form.setFieldsValue({
                phone: formData.phone,
                email: formData.email,
                poFiles: formData.purchaseOrderInfo?.poFiles,
                saveShippingDefaults: !!formData.saveShippingDefaults,

                shippingAccountNumber: formData.shippingAccountNumber,
                carrier: formData.carrier,
                shippingMethod: formData.shippingMethod,
                notes: formData.notes,
            });
        }, [
            form,
            formData.phone,
            formData.email,
            formData.purchaseOrderInfo?.poFiles,
            formData.shippingAccountNumber,
            formData.carrier,
            formData.shippingMethod,
            formData.saveShippingDefaults,
            formData.notes,
        ]);
        return (
            <Card className="shadow-sm" data-testid="customer-order-checkout-summary">
                <Title level={4} style={{ margin: 0 }}>
                    Order Summary
                </Title>

                <div style={{ marginTop: 12 }}>
                    <div className="flex justify-between text-sm mb-1">
                        <span>Subtotal</span>
                        <span>
                            <Price value={subtotal} />
                        </span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                        <span>Discount</span>
                        <span>
                            <Price value={discount} />
                        </span>
                    </div>
                    <div className="flex justify-between text-sm mb-1">
                        <span>Tax</span>
                        <span>
                            <Price value={tax} />
                        </span>
                    </div>

                    <Divider style={{ margin: '8px 0' }} />

                    <div className="flex justify-between items-center">
                        <span className="text-lg font-semibold">Total</span>
                        <span className="text-2xl font-bold" style={{ fontWeight: 600, color: '#1677ff' }}>
                            <Price value={total} />
                        </span>
                    </div>
                </div>

                <Divider />

                {/* Shipping */}
                <Text strong>Shipping Information</Text>
                {isReorder ? (
                    <div style={{ marginTop: 8 }} className="space-y-3">
                        <ReadOnlyAddressCard
                            title="Shipping Address"
                            name={(order as any)?.shipping_name}
                            street={(order as any)?.shipping_street}
                            city={(order as any)?.shipping_city}
                            state={(order as any)?.shipping_state}
                            zip={(order as any)?.shipping_zip_code}
                            country={(order as any)?.shipping_country}
                        />

                        <ShippingPrefsReadOnly
                            shippingAccountNumber={String((order as any)?.shipping_account_number ?? '')}
                            carrier={String((order as any)?.carrier ?? '')}
                            shippingMethod={String((order as any)?.shipping_method ?? '')}
                            readOnly
                            showEdit={false}
                        />
                    </div>
                ) : (
                    <>
                        <Form
                            form={form}
                            layout="vertical"
                            size="small"
                            style={{ marginTop: 8 }}
                            onValuesChange={(changed, all) => {
                                if (typeof all.phone === 'string') onFormUpdate('phone', all.phone);

                                if ('poFiles' in changed) {
                                    onFormUpdate('purchaseOrderInfo', { poFiles: all.poFiles ?? [] });
                                }

                                if ('saveShippingDefaults' in changed) {
                                    onFormUpdate('saveShippingDefaults', !!all.saveShippingDefaults);
                                }

                                if ('shippingAccountNumber' in changed)
                                    onFormUpdate('shippingAccountNumber', all.shippingAccountNumber);
                                if ('carrier' in changed) onFormUpdate('carrier', all.carrier);
                                if ('shippingMethod' in changed) onFormUpdate('shippingMethod', all.shippingMethod);
                            }}
                        >
                            <Form.Item label="Shipping Address *">
                                <ShowDefault
                                    type="Shipping"
                                    currentAddress={selectedShippingAddress}
                                    addresses={shippingAddresses}
                                    onAddNew={() => {
                                        setShippingModalInitial(undefined);
                                        setShippingModalOpen(true);
                                    }}
                                    onEdit={addr => {
                                        setShippingModalInitial(addr);
                                        setShippingModalOpen(true);
                                    }}
                                    onSelect={async addr => {
                                        const id = addr?.id;
                                        if (!id || !addr) return;

                                        onFormUpdate('shippingAddressId', id);
                                        onShippingAddressChange(id);

                                        try {
                                            await patchOrderAddressFromAddress('shipping', addr);
                                        } catch (err: any) {
                                            notification.error({
                                                message: 'Could not update shipping address on order',
                                                description: <ErrorRender error={err} />,
                                            });
                                        }

                                        if (formData.billingSameAsShipping) {
                                            onFormUpdate('billingAddressId', id);
                                            onBillingAddressChange(id);

                                            // IMPORTANT: also patch billing fields from the same shipping addr
                                            try {
                                                await patchOrderAddressFromAddress('billing', addr);
                                            } catch (err: any) {
                                                notification.error({
                                                    message: 'Could not update billing address on order',
                                                    description: <ErrorRender error={err} />,
                                                });
                                            }
                                        }
                                    }}
                                />
                            </Form.Item>

                            {/* Shipping */}

                            {!isReorder && editingShippingPrefs ? (
                                <div className="rounded-lg border border-slate-200 bg-white p-3">
                                    <div className="flex items-center justify-between mb-2">
                                        <Text strong>Shipping Preferences</Text>
                                        <Button
                                            type="link"
                                            size="small"
                                            className="p-0 h-auto"
                                            onClick={() => setEditingShippingPrefs(false)}
                                        >
                                            Done
                                        </Button>
                                    </div>

                                    <Form.Item label="Shipping Account (Optional)" name="shippingAccountNumber">
                                        <Input placeholder="Shipping Account Number" />
                                    </Form.Item>

                                    <Form.Item
                                        label="Carrier"
                                        name="carrier"
                                        trigger="onChange"
                                        valuePropName="value"
                                        getValueFromEvent={normalizeSelectValue}
                                    >
                                        <ShippingCarriers size="large" />
                                    </Form.Item>

                                    <Form.Item
                                        label="Shipping Method"
                                        name="shippingMethod"
                                        trigger="onChange"
                                        valuePropName="value"
                                        getValueFromEvent={normalizeSelectValue}
                                    >
                                        <ShippingMethods size="large" />
                                    </Form.Item>

                                    <Form.Item
                                        name="saveShippingDefaults"
                                        valuePropName="checked"
                                        style={{ marginBottom: 8 }}
                                    >
                                        <Checkbox>Save these as my default</Checkbox>
                                    </Form.Item>

                                    <div style={{ marginTop: 4 }}>
                                        <Text type="secondary" style={{ fontSize: 12 }}>
                                            Saves carrier, shipping method, and shipping account number for future
                                            orders.
                                        </Text>
                                    </div>
                                </div>
                            ) : (
                                <ShippingPrefsReadOnly
                                    shippingAccountNumber={
                                        isReorder ? roShippingAccount : formData.shippingAccountNumber
                                    }
                                    carrier={isReorder ? roCarrier : formData.carrier}
                                    shippingMethod={isReorder ? roMethod : formData.shippingMethod}
                                    readOnly={isReorder}
                                    showEdit={!isReorder && canEditShippingPrefs}
                                    onEdit={() => setEditingShippingPrefs(true)}
                                />
                            )}

                            <Form.Item
                                name="phone"
                                label="Phone *"
                                rules={[
                                    {
                                        validator: (_, value) => validatePhoneNumber(value, false),
                                    },
                                ]}
                                hasFeedback={true}
                            >
                                <Input placeholder="Phone" />
                            </Form.Item>
                        </Form>

                        <AddAddressModal
                            open={shippingModalOpen}
                            type="Shipping"
                            onClose={() => {
                                setShippingModalOpen(false);
                                setShippingModalInitial(undefined);
                            }}
                            initial={shippingModalInitial}
                            onSaved={addr => {
                                const id = addr?.id;
                                if (!id) return;

                                onFormUpdate('shippingAddressId', id);
                                onShippingAddressChange(id);

                                if (formData.billingSameAsShipping) {
                                    onFormUpdate('billingAddressId', undefined);
                                }
                            }}
                        />
                    </>
                )}

                <Divider />

                {/* Billing */}
                <Text strong>Billing Information</Text>
                {isReorder ? (
                    <div style={{ marginTop: 8 }}>
                        <ReadOnlyAddressCard
                            title="Billing Address"
                            name={(order as any)?.billing_name}
                            street={(order as any)?.billing_street}
                            city={(order as any)?.billing_city}
                            state={(order as any)?.billing_state}
                            zip={(order as any)?.billing_zip_code}
                            country={(order as any)?.billing_country}
                        />
                    </div>
                ) : (
                    <>
                        <div style={{ marginTop: 8 }}>
                            <Checkbox
                                checked={formData.billingSameAsShipping}
                                onChange={async e => {
                                    const same = e.target.checked;
                                    onBillingSameAsShippingChange(same);

                                    if (same) {
                                        const shipAddr = selectedShippingAddress ?? currentShippingAddress;
                                        const shipId = formData.shippingAddressId ?? shipAddr?.id;

                                        if (shipId) {
                                            onFormUpdate('billingAddressId', shipId);
                                            onBillingAddressChange(shipId);
                                        }

                                        if (shipAddr) {
                                            try {
                                                await patchOrderAddressFromAddress('billing', shipAddr);
                                            } catch (err: any) {
                                                notification.error({
                                                    message: 'Could not update billing address on order',
                                                    description: <ErrorRender error={err} />,
                                                });
                                            }
                                        }

                                        return;
                                    }

                                    const billAddr = selectedBillingAddress ?? currentBillingAddress;
                                    const billId = billAddr?.id;

                                    if (billId) {
                                        onFormUpdate('billingAddressId', billId);
                                        onBillingAddressChange(billId);
                                    } else {
                                        onFormUpdate('billingAddressId', undefined);
                                    }

                                    if (billAddr) {
                                        try {
                                            await patchOrderAddressFromAddress('billing', billAddr);
                                        } catch (err: any) {
                                            notification.error({
                                                message: 'Could not update billing address on order',
                                                description: <ErrorRender error={err} />,
                                            });
                                        }
                                    }
                                }}
                            >
                                Same as shipping address
                            </Checkbox>
                        </div>

                        {!formData.billingSameAsShipping && (
                            <>
                                <Form layout="vertical" size="small" style={{ marginTop: 8 }}>
                                    <Form.Item label="Billing Address *">
                                        <ShowDefault
                                            type="Billing"
                                            currentAddress={selectedBillingAddress}
                                            addresses={billingAddresses}
                                            onAddNew={() => {
                                                setBillingModalInitial(undefined);
                                                setBillingModalOpen(true);
                                            }}
                                            onEdit={addr => {
                                                setBillingModalInitial(addr);
                                                setBillingModalOpen(true);
                                            }}
                                            onSelect={async addr => {
                                                const id = addr?.id;
                                                if (!id || !addr) return;

                                                onFormUpdate('billingAddressId', id);
                                                onBillingAddressChange(id);

                                                try {
                                                    await patchOrderAddressFromAddress('billing', addr);
                                                } catch (err: any) {
                                                    notification.error({
                                                        message: 'Could not update billing address on order',
                                                        description: <ErrorRender error={err} />,
                                                    });
                                                }
                                            }}
                                        />
                                    </Form.Item>
                                </Form>

                                <AddAddressModal
                                    open={billingModalOpen}
                                    type="Billing"
                                    onClose={() => {
                                        setBillingModalOpen(false);
                                        setBillingModalInitial(undefined);
                                    }}
                                    initial={billingModalInitial}
                                    onSaved={addr => {
                                        const id = addr?.id;
                                        if (!id) return;

                                        onFormUpdate('billingAddressId', id);
                                        onBillingAddressChange(id);
                                    }}
                                />
                            </>
                        )}
                    </>
                )}
                <Divider />

                {isReorder && !isHobbyist ? (
                    <Form
                        form={form}
                        layout="vertical"
                        size="small"
                        style={{ marginTop: 10 }}
                        onValuesChange={(changed, all) => {
                            if ('notes' in changed) onFormUpdate('notes', all.notes ?? '');
                        }}
                    >
                        <Form.Item label="Order Comments" name="notes">
                            <Input.TextArea placeholder="Type here..." autoSize={{ minRows: 3, maxRows: 6 }} />
                        </Form.Item>
                    </Form>
                ) : null}

                <Text strong>Payment Method</Text>
                {isReorder && isHobbyist ? (
                    <div style={{ marginTop: 10 }}>
                        <SquarePaymentForm
                            buttonRef={buttonRef}
                            app_id={SQUARE_APP_ID}
                            location_id={SQUARE_LOCATION_ID}
                            script_url={SQUARE_ENVIRONMENT_URL}
                            onPayment={sourceId => {
                                if (!sourceId) return;
                                if (!allowSquareSubmitRef.current) return;

                                allowSquareSubmitRef.current = false;
                                onCreditCardToken(sourceId, lastValidatedRef.current);
                            }}
                        />
                    </div>
                ) : (
                    <div style={{ marginTop: 8 }}>
                        <Radio.Group
                            value={formData.paymentMethod}
                            onChange={e => onFormUpdate('paymentMethod', e.target.value)}
                            data-testid="customer-order-payment-method"
                        >
                            <Radio value="credit_card" data-testid="customer-order-payment-credit-card">
                                Credit Card
                            </Radio>
                            {!isHobbyist ? (
                                <Radio
                                    value="purchase_order"
                                    disabled={isHobbyist}
                                    data-testid="customer-order-payment-purchase-order"
                                >
                                    Purchase Order
                                </Radio>
                            ) : null}
                        </Radio.Group>

                        {formData.paymentMethod === 'purchase_order' && (
                            <Form layout="vertical" size="small" style={{ marginTop: 10 }}>
                                <Form.Item label="Invoice / Purchase Order Upload *" required>
                                    <div data-testid="customer-order-po-upload">
                                        <UploadFile
                                            multiple
                                            value={formData.purchaseOrderInfo?.poFiles}
                                            fileRowTestIdPrefix="customer-order-po-upload-file"
                                            showUploadList
                                            onChange={(fileList: any[]) => {
                                                onFormUpdate('purchaseOrderInfo', { poFiles: fileList });
                                                form.setFieldsValue({ poFiles: fileList ?? [] });
                                            }}
                                        />
                                    </div>

                                    {poAttempted && !(formData.purchaseOrderInfo?.poFiles?.length ?? 0) ? (
                                        <div style={{ marginTop: 6 }}>
                                            <Text type="danger" style={{ fontSize: 12 }}>
                                                Please upload your purchase order / invoice to continue.
                                            </Text>
                                        </div>
                                    ) : null}
                                </Form.Item>
                            </Form>
                        )}

                        {formData.paymentMethod === 'credit_card' && (
                            <div style={{ marginTop: 10 }}>
                                <SquarePaymentForm
                                    buttonRef={buttonRef}
                                    app_id={SQUARE_APP_ID}
                                    location_id={SQUARE_LOCATION_ID}
                                    script_url={SQUARE_ENVIRONMENT_URL}
                                    onPayment={sourceId => {
                                        squareRespondedRef.current = true;

                                        const errors = [...pendingMissingRef.current];

                                        if (!sourceId) {
                                            errors.push('Credit card details');
                                        }

                                        if (errors.length > 0) {
                                            notification.error({
                                                message: 'Please fill all required fields',
                                                description: Array.from(new Set(errors)).join(', '),
                                            });
                                            return;
                                        }

                                        if (!allowSquareSubmitRef.current) return;

                                        allowSquareSubmitRef.current = false;

                                        onCreditCardToken(sourceId as string, lastValidatedRef.current);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                )}

                <Divider />

                <div className="flex justify-between items-center" style={{ marginBottom: 10 }}>
                    <Text strong>Grand Total</Text>
                    <Text strong style={{ fontSize: 18 }}>
                        <Price value={total} />
                    </Text>
                </div>

                <Alert
                    type="info"
                    showIcon
                    icon={<InfoCircleOutlined />}
                    message={
                        <span>
                            Your order will automatically include a <Text strong>Material Certificate</Text> and{' '}
                            <Text strong>Certificate of Conformance</Text>.
                        </span>
                    }
                    style={{
                        marginBottom: 12,
                        border: '1px solid #91caff',
                        background: '#e6f4ff',
                    }}
                />

                <Button
                    type="primary"
                    size="large"
                    block
                    data-testid="customer-order-submit"
                    loading={submitting}
                    disabled={submitting}
                    onClick={async () => {
                        // Pull the *actual* current values from the AntD form (source of truth)
                        const live = form.getFieldsValue([
                            'shippingAccountNumber',
                            'carrier',
                            'shippingMethod',
                            'saveShippingDefaults',
                            'phone',
                            'poFiles',
                        ]);

                        if (!isReorder) setAckAttempted(true);
                        setPoAttempted(formData.paymentMethod === 'purchase_order');

                        const missing: string[] = [];

                        // Acks
                        missing.push(...getMissingAcks());

                        // Addresses
                        const shipId = formData.shippingAddressId ?? currentShippingAddress?.id;
                        if (!shipId) missing.push('Shipping address');

                        if (!formData.billingSameAsShipping) {
                            const billId = formData.billingAddressId ?? currentBillingAddress?.id;
                            if (!billId) missing.push('Billing address');
                        }

                        let validated: { phone?: string; email?: string } = {};
                        if (isReorder) {
                            const phoneTrim = String(formData.phone ?? '').trim();
                            if (!phoneTrim) missing.push('Phone');
                            else validated.phone = phoneTrim;
                        } else {
                            try {
                                const v = await form.validateFields(['phone']);
                                validated = {
                                    phone: String(v.phone ?? '').trim(),
                                };
                            } catch (err: any) {
                                const fields = (err?.errorFields ?? []).map((f: any) => String(f?.name?.[0] ?? ''));
                                if (fields.includes('phone')) missing.push('Phone');
                            }
                        }

                        if (formData.paymentMethod === 'purchase_order') {
                            const files = formData.purchaseOrderInfo?.poFiles ?? [];
                            if (!files.length) missing.push('Purchase order upload');
                        }

                        const canProceedNonSquare = missing.length === 0;

                        const shipIdLive = formData.shippingAddressId ?? currentShippingAddress?.id;

                        const billingSameLive = !!formData.billingSameAsShipping;

                        const billIdLive = billingSameLive
                            ? shipIdLive
                            : formData.billingAddressId ?? currentBillingAddress?.id;

                        const overrides: SubmitOverrides = {
                            ...validated,
                            shippingAccountNumber: live.shippingAccountNumber,
                            carrier: live.carrier,
                            shippingMethod: live.shippingMethod,
                            saveShippingDefaults: !!live.saveShippingDefaults,
                            shippingAddressId: shipIdLive,
                            billingSameAsShipping: billingSameLive,
                            billingAddressId: billIdLive,
                        };

                        // Still sync local state for UI/persistence (not relied on for submit)
                        onFormUpdate('shippingAccountNumber', overrides.shippingAccountNumber as any);
                        onFormUpdate('carrier', overrides.carrier as any);
                        onFormUpdate('shippingMethod', overrides.shippingMethod as any);
                        if (validated.phone != null) onFormUpdate('phone', validated.phone as any);

                        // Square uses this object when it actually submits
                        lastValidatedRef.current = overrides;
                        allowSquareSubmitRef.current = canProceedNonSquare;

                        if (formData.paymentMethod === 'credit_card') {
                            // ALWAYS trigger Square to validate card
                            lastValidatedRef.current = overrides;
                            allowSquareSubmitRef.current = missing.length === 0;

                            // store missing for later merge
                            pendingMissingRef.current = missing;

                            buttonRef.current?.click();
                            return;
                        }

                        if (missing.length) {
                            notification.destroy();
                            notification.error({
                                message: 'Please fill all required fields',
                                description: Array.from(new Set(missing)).join(', '),
                            });
                            return;
                        }

                        // IMPORTANT: pass overrides (includes shipping fields) so parent payload is correct
                        onSubmitPO('PURCHASE_ORDER', overrides);
                    }}
                >
                    {formData.paymentMethod === 'purchase_order' ? 'Submit for Review' : 'Pay & Place Order'}
                </Button>
                {!isReorder && (
                    <Form layout="vertical" size="small" style={{ marginBottom: 10 }}>
                        {isHobbyist && (
                            <Form.Item
                                style={{ marginBottom: 8 }}
                                validateStatus={
                                    ackError(acks.terms, 'Please accept the terms and conditions') ? 'error' : undefined
                                }
                                help={ackError(acks.terms, 'Please accept the terms and conditions')}
                            >
                                <Checkbox checked={acks.terms} onChange={e => setAck('terms', e.target.checked)}>
                                    I have read and accept the{' '}
                                    <a
                                        className="font-bold underline"
                                        href="https://parts-badger.com/terms-and-conditions/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        terms and conditions
                                    </a>
                                    .
                                </Checkbox>
                            </Form.Item>
                        )}

                        {/* Always shown */}
                        <Form.Item
                            style={{ marginBottom: 8 }}
                            validateStatus={
                                ackError(acks.privacy, 'Please check to acknowledge our Privacy & Terms Policy.')
                                    ? 'error'
                                    : undefined
                            }
                            help={ackError(acks.privacy, 'Please check to acknowledge our Privacy & Terms Policy.')}
                        >
                            <Checkbox
                                checked={acks.privacy}
                                onChange={e => setAck('privacy', e.target.checked)}
                                data-testid="customer-order-ack-privacy"
                            >
                                Please check to acknowledge our{' '}
                                <a
                                    className="font-bold underline"
                                    href="https://parts-badger.com/terms-and-conditions/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Privacy &amp; Terms Policy
                                </a>
                                .
                            </Checkbox>
                        </Form.Item>

                        {isHobbyist && (
                            <Form.Item
                                style={{ marginBottom: 8 }}
                                validateStatus={
                                    ackError(
                                        acks.authoritative,
                                        'Please acknowledge that this sales order is the authoritative document for your order.'
                                    )
                                        ? 'error'
                                        : undefined
                                }
                                help={ackError(
                                    acks.authoritative,
                                    'Please acknowledge that this sales order is the authoritative document for your order.'
                                )}
                            >
                                <Checkbox
                                    checked={acks.authoritative}
                                    onChange={e => setAck('authoritative', e.target.checked)}
                                >
                                    I acknowledge that information in this sales order shall be the authority for the
                                    order, superseding any conflicting information provided in email, design files, note
                                    files for this order, or via any other method of communication.
                                </Checkbox>
                            </Form.Item>
                        )}

                        {/* Always shown */}
                        <Form.Item
                            style={{ marginBottom: 0 }}
                            validateStatus={
                                ackError(
                                    acks.dimensional,
                                    'Please acknowledge the dimensional tolerance terms to proceed with the order'
                                )
                                    ? 'error'
                                    : undefined
                            }
                            help={ackError(
                                acks.dimensional,
                                'Please acknowledge the dimensional tolerance terms to proceed with the order'
                            )}
                        >
                            <Checkbox
                                checked={acks.dimensional}
                                onChange={e => setAck('dimensional', e.target.checked)}
                                data-testid="customer-order-ack-dimensional"
                            >
                                I understand that dimensional tolerances are quoted to print unless otherwise noted on
                                the quote. If no print is provided, a tolerance table is missing, or if significant
                                digits are not the same, then all dimensions will be held to one of the tables on the
                                Terms &amp; Conditions.
                            </Checkbox>
                        </Form.Item>
                    </Form>
                )}
            </Card>
        );
    }
);

export default OrderCheckoutSummary;
